/**
 * OrdersOperationsView — dispatcher's "operations in course" screen.
 *
 * One row per vehicle currently executing OTs. Designed for fleets where
 * 100+ vehicles can run 500+ OTs concurrently:
 *   - Backend pre-aggregates by vehicleId (no client-side fold of 500 rows)
 *   - Live GPS comes from the Redux `vehicles` slice (kept fresh by WS),
 *     so position updates only re-render the affected row
 *   - Each row is a memoised component so unrelated WS ticks don't cascade
 *     into a full re-render of the table
 *
 * Click a row → opens the existing LiveRoutePanel for that vehicle's
 * current OT, where the dispatcher can follow the truck in real-time.
 */
import React, { useEffect, useMemo, useState, useContext, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { Box, Typography, IconButton, Tooltip, LinearProgress, Chip, TextField, InputAdornment } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import LocalShippingIcon from '@material-ui/icons/LocalShipping';
import PersonIcon from '@material-ui/icons/Person';
import LocationOnIcon from '@material-ui/icons/LocationOn';
import ScheduleIcon from '@material-ui/icons/Schedule';
import WarningIcon from '@material-ui/icons/Warning';
import SearchIcon from '@material-ui/icons/Search';
import RefreshIcon from '@material-ui/icons/Refresh';
import EditIcon from '@material-ui/icons/Edit';
import { AuthContext } from '../../App';
import { getSocket } from '../../utils/websocket';
import { otLabel } from '../../utils/otLabel';
import DriverQuickEditDialog from '../common/DriverQuickEditDialog';

const useStyles = makeStyles(() => ({
  root: { display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 },
  toolbar: {
    display: 'flex', alignItems: 'center', gap: 10,
    padding: '10px 16px',
    borderBottom: '1px solid #e2e8f0',
    background: '#fff',
    flexShrink: 0,
  },
  scroll: { flex: 1, overflow: 'auto', minHeight: 0, background: '#f8fafc' },
  header: {
    display: 'grid',
    gridTemplateColumns: '120px 180px 1fr 200px 120px 120px 120px 80px',
    gap: 8, padding: '8px 16px',
    background: '#f1f5f9',
    borderBottom: '1px solid #e2e8f0',
    position: 'sticky', top: 0, zIndex: 1,
  },
  headerCell: {
    fontSize: '0.6rem', fontWeight: 800,
    color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em',
  },
  row: {
    display: 'grid',
    gridTemplateColumns: '120px 180px 1fr 200px 120px 120px 120px 80px',
    gap: 8, padding: '10px 16px',
    background: '#fff',
    borderBottom: '1px solid #e2e8f0',
    cursor: 'pointer',
    transition: 'background 0.1s',
    '&:hover': { background: '#eff6ff' },
  },
  rowDelayed: { borderLeft: '3px solid #ef4444', background: '#fef2f2' },
  rowDone: { background: '#f0fdf4', borderLeft: '3px solid #22c55e' },
  cell: { display: 'flex', alignItems: 'center', minWidth: 0 },
  plate: {
    fontSize: '0.82rem', fontWeight: 800, color: '#0f172a',
    fontFamily: '"Roboto Mono", monospace',
    background: '#fef3c7', padding: '2px 8px', borderRadius: 5,
  },
  driverName: { fontSize: '0.78rem', fontWeight: 700, color: '#0f172a' },
  driverPhone: { fontSize: '0.62rem', color: '#64748b' },
  emptyState: {
    flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
    flexDirection: 'column', color: '#94a3b8', padding: 40, gap: 8,
  },
}));

function fmtTime(d) {
  if (!d) return '—';
  return new Date(d).toLocaleTimeString('pt-PT', { hour: '2-digit', minute: '2-digit' });
}

function ageMin(d) {
  if (!d) return null;
  return Math.max(0, Math.floor((Date.now() - new Date(d).getTime()) / 60000));
}

/**
 * Single vehicle row. Memoised: re-renders only when its own data or its
 * vehicle's GPS position changes — avoids cascading re-renders when other
 * vehicles' positions tick over the WS.
 */
const OperationRow = React.memo(function OperationRow({ entry, vehiclePos, onOpenLive, onEditDriver }) {
  const classes = useStyles();
  const progress = entry.totalOTs > 0 ? Math.round((entry.deliveredOTs / entry.totalOTs) * 100) : 0;
  const allDone = entry.totalOTs > 0 && entry.deliveredOTs === entry.totalOTs;
  const fixAge = ageMin(vehiclePos?.fixTime);

  return (
    <Box
      className={`${classes.row} ${entry.delayed ? classes.rowDelayed : ''} ${allDone ? classes.rowDone : ''}`}
      onClick={() => onOpenLive(entry)}
    >
      {/* Veículo */}
      <Box className={classes.cell}>
        <Typography className={classes.plate}>{entry.vehiclePlate || '—'}</Typography>
      </Box>

      {/* Motorista */}
      <Box className={classes.cell} style={{ flexDirection: 'column', alignItems: 'flex-start' }}>
        <Box style={{ display: 'flex', alignItems: 'center', gap: 4, maxWidth: '100%' }}>
          <Typography className={classes.driverName} noWrap>
            {entry.driverName || `#${entry.driverId || '—'}`}
          </Typography>
          {entry.driverId && onEditDriver && (
            <Tooltip title="Editar motorista">
              <IconButton size="small" onClick={(e) => { e.stopPropagation(); onEditDriver(entry); }} style={{ padding: 2 }}>
                <EditIcon style={{ fontSize: 14, color: '#94a3b8' }} />
              </IconButton>
            </Tooltip>
          )}
        </Box>
        {entry.driverPhone && <Typography className={classes.driverPhone}>{entry.driverPhone}</Typography>}
      </Box>

      {/* OT em curso + próxima */}
      <Box className={classes.cell} style={{ flexDirection: 'column', alignItems: 'flex-start', minWidth: 0 }}>
        {entry.currentOT ? (
          <>
            <Typography style={{ fontSize: '0.74rem', fontWeight: 700, color: '#1e40af' }} noWrap>
              ▶ {otLabel(entry.currentOT)} · {entry.currentOT.deliveryName || entry.currentOT.deliveryAddress || ''}
            </Typography>
            <Typography style={{ fontSize: '0.62rem', color: '#64748b' }}>
              ETA {fmtTime(entry.currentOT.estimatedArrival)}
              {entry.currentOT.deliveryWindowTo && (
                <span> · janela até {fmtTime(entry.currentOT.deliveryWindowTo)}</span>
              )}
            </Typography>
          </>
        ) : entry.nextOT ? (
          <>
            <Typography style={{ fontSize: '0.74rem', fontWeight: 600, color: '#64748b' }} noWrap>
              Próxima: {otLabel(entry.nextOT)} · {entry.nextOT.deliveryName || ''}
            </Typography>
          </>
        ) : allDone ? (
          <Typography style={{ fontSize: '0.74rem', fontWeight: 700, color: '#16a34a' }}>
            ✓ Rota concluída
          </Typography>
        ) : (
          <Typography style={{ fontSize: '0.7rem', color: '#94a3b8' }}>—</Typography>
        )}
      </Box>

      {/* Progresso */}
      <Box className={classes.cell} style={{ flexDirection: 'column', alignItems: 'stretch', gap: 3, minWidth: 0 }}>
        <Box style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography style={{ fontSize: '0.7rem', fontWeight: 700, color: '#0f172a' }}>
            {entry.deliveredOTs}/{entry.totalOTs}
          </Typography>
          <Typography style={{ fontSize: '0.6rem', color: '#64748b' }}>{progress}%</Typography>
        </Box>
        <LinearProgress
          variant="determinate"
          value={progress}
          style={{ height: 5, borderRadius: 3 }}
        />
      </Box>

      {/* Posição GPS */}
      <Box className={classes.cell}>
        {vehiclePos?.latitude != null ? (
          <Tooltip title={fixAge != null ? `Última posição há ${fixAge} min` : 'Posição actual'}>
            <Box style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <LocationOnIcon style={{
                fontSize: 14,
                color: fixAge != null && fixAge < 5 ? '#22c55e' : fixAge < 30 ? '#f59e0b' : '#94a3b8',
              }} />
              <Typography style={{ fontSize: '0.6rem', color: '#0f172a' }}>
                {fixAge == null ? 'agora' : fixAge === 0 ? 'agora' : `há ${fixAge}m`}
              </Typography>
            </Box>
          </Tooltip>
        ) : (
          <Typography style={{ fontSize: '0.6rem', color: '#cbd5e1', fontStyle: 'italic' }}>sem GPS</Typography>
        )}
      </Box>

      {/* Última entrega */}
      <Box className={classes.cell}>
        {entry.lastDeliveredAt ? (
          <Box>
            <Typography style={{ fontSize: '0.66rem', color: '#16a34a', fontWeight: 700 }}>
              ✓ {fmtTime(entry.lastDeliveredAt)}
            </Typography>
          </Box>
        ) : (
          <Typography style={{ fontSize: '0.62rem', color: '#cbd5e1' }}>—</Typography>
        )}
      </Box>

      {/* Estado */}
      <Box className={classes.cell}>
        {entry.delayed && (
          <Chip
            size="small"
            icon={<WarningIcon style={{ fontSize: 12, color: '#fff' }} />}
            label="Atrasado"
            style={{ background: '#ef4444', color: '#fff', height: 20, fontSize: '0.6rem', fontWeight: 700 }}
          />
        )}
        {!entry.delayed && entry.highestPriority === 1 && (
          <Chip size="small" label="Urgente" style={{ background: '#fef3c7', color: '#92400e', height: 20, fontSize: '0.6rem', fontWeight: 700 }} />
        )}
        {!entry.delayed && entry.highestPriority !== 1 && entry.serviceType === 'assistance' && (
          <Chip size="small" label="Assist." style={{ background: '#dcfce7', color: '#166534', height: 20, fontSize: '0.6rem', fontWeight: 700 }} />
        )}
      </Box>

      {/* Acções */}
      <Box className={classes.cell} style={{ justifyContent: 'flex-end' }}>
        <Tooltip title="Abrir vista live">
          <IconButton size="small" onClick={(e) => { e.stopPropagation(); onOpenLive(entry); }}>
            <Box style={{
              width: 8, height: 8, borderRadius: '50%',
              background: '#ef4444',
              boxShadow: '0 0 6px rgba(239,68,68,0.6), 0 0 0 2px rgba(239,68,68,0.25)',
            }} />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
}, (prev, next) => {
  // Custom equality — only re-render if the entry data or this row's
  // vehicle position changed. Other vehicles' GPS ticks won't trigger
  // re-renders here.
  if (prev.entry !== next.entry) return false;
  if (prev.vehiclePos?.latitude !== next.vehiclePos?.latitude) return false;
  if (prev.vehiclePos?.longitude !== next.vehiclePos?.longitude) return false;
  if (prev.vehiclePos?.fixTime !== next.vehiclePos?.fixTime) return false;
  return true;
});

export default function OrdersOperationsView({ onOpenLive, lang = 'pt' }) {
  const classes = useStyles();
  const { token } = useContext(AuthContext);
  const [summary, setSummary] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [autoRefresh, setAutoRefresh] = useState(true);
  const vehicles = useSelector(s => s.vehicles?.list || []);
  // Motoristas (registo completo) para a edição rápida — mapa por id.
  const [driversById, setDriversById] = useState({});
  const [editDriver, setEditDriver] = useState(null);

  const fetchDrivers = useCallback(() => {
    if (!token) return;
    fetch('/planner-api/drivers', { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : [])
      .then(list => {
        const m = {};
        (Array.isArray(list) ? list : []).forEach(d => { m[String(d.id)] = d; });
        setDriversById(m);
      })
      .catch(() => {});
  }, [token]);

  const refresh = useCallback(() => {
    if (!token) return;
    setLoading(true);
    fetch('/planner-api/transport-orders/operations-summary', {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.ok ? r.json() : [])
      .then(data => setSummary(Array.isArray(data) ? data : []))
      .catch(() => setSummary([]))
      .finally(() => setLoading(false));
  }, [token]);

  useEffect(() => { fetchDrivers(); }, [fetchDrivers]);

  // Abre o diálogo de edição com o registo completo do motorista (fallback ao
  // que a linha já traz, quando não está no mapa — ex.: só nome/telefone).
  const openEditDriver = useCallback((entry) => {
    const full = entry?.driverId != null ? driversById[String(entry.driverId)] : null;
    setEditDriver(full || (entry?.driverId
      ? { id: entry.driverId, name: entry.driverName, phone: entry.driverPhone }
      : null));
  }, [driversById]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Auto-refresh every 30s when enabled. The view is meant for monitoring,
  // not real-time animation — that's what LiveRoutePanel is for.
  useEffect(() => {
    if (!autoRefresh) return;
    const id = setInterval(refresh, 30000);
    return () => clearInterval(id);
  }, [autoRefresh, refresh]);

  // WebSocket: nudge a refresh on relevant events. We don't react to every
  // tick (positions are picked up via the vehicles slice), only to events
  // that actually change the summary shape (delivery, route status changes).
  useEffect(() => {
    const sock = getSocket();
    if (!sock) return;
    const handler = () => refresh();
    sock.on('route:status', handler);
    sock.on('order:status', handler);
    sock.on('order:delivered', handler);
    return () => {
      sock.off('route:status', handler);
      sock.off('order:status', handler);
      sock.off('order:delivered', handler);
    };
  }, [refresh]);

  // Build a quick vehicleId→position map so each row can grab its own pos
  // in O(1). The map is rebuilt only when `vehicles` changes (most slice
  // updates are a single field change so the array reference rotates,
  // but map rebuild for ~100 entries is cheap).
  const positionByVehicleId = useMemo(() => {
    const m = new Map();
    for (const v of vehicles) {
      if (v?.id != null && v?.position) m.set(Number(v.id), v.position);
    }
    return m;
  }, [vehicles]);

  const filtered = useMemo(() => {
    if (!search.trim()) return summary;
    const q = search.trim().toLowerCase();
    return summary.filter(e =>
      String(e.vehiclePlate || '').toLowerCase().includes(q) ||
      String(e.driverName || '').toLowerCase().includes(q) ||
      String(e.currentOT?.orderNumber || '').toLowerCase().includes(q) ||
      String(e.currentOT?.customRef || '').toLowerCase().includes(q) ||
      String(e.currentOT?.deliveryName || '').toLowerCase().includes(q)
    );
  }, [summary, search]);

  const totals = useMemo(() => {
    let delayed = 0, completed = 0, totalOTs = 0, deliveredOTs = 0;
    for (const e of summary) {
      if (e.delayed) delayed += 1;
      if (e.totalOTs > 0 && e.deliveredOTs === e.totalOTs) completed += 1;
      totalOTs += e.totalOTs;
      deliveredOTs += e.deliveredOTs;
    }
    return { delayed, completed, totalOTs, deliveredOTs, vehicles: summary.length };
  }, [summary]);

  return (
    <Box className={classes.root}>
      <Box className={classes.toolbar}>
        <LocalShippingIcon style={{ fontSize: 20, color: '#1e40af' }} />
        <Typography style={{ fontSize: '0.92rem', fontWeight: 800, color: '#0f172a' }}>
          Operações em curso
        </Typography>
        <Chip size="small" label={`${totals.vehicles} viaturas`} style={{ background: '#dbeafe', color: '#1e40af', fontWeight: 700, fontSize: '0.66rem', height: 22 }} />
        <Chip size="small" label={`${totals.deliveredOTs}/${totals.totalOTs} OTs`} style={{ background: '#dcfce7', color: '#166534', fontWeight: 700, fontSize: '0.66rem', height: 22 }} />
        {totals.delayed > 0 && (
          <Chip size="small" icon={<WarningIcon style={{ fontSize: 12, color: '#fff' }} />} label={`${totals.delayed} atrasadas`} style={{ background: '#ef4444', color: '#fff', fontWeight: 700, fontSize: '0.66rem', height: 22 }} />
        )}
        <Box style={{ flex: 1 }} />
        <TextField
          size="small"
          variant="outlined"
          placeholder="Pesquisar matrícula, motorista, OT..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: <InputAdornment position="start"><SearchIcon style={{ fontSize: 16, color: '#94a3b8' }} /></InputAdornment>,
            style: { fontSize: '0.78rem', height: 32 },
          }}
          style={{ width: 280 }}
        />
        <Tooltip title={autoRefresh ? 'Auto-refresh activo (30s)' : 'Auto-refresh desactivado'}>
          <IconButton size="small" onClick={() => setAutoRefresh(!autoRefresh)}
            style={{ background: autoRefresh ? '#dcfce7' : 'transparent' }}>
            <RefreshIcon style={{ fontSize: 16, color: autoRefresh ? '#16a34a' : '#94a3b8' }} />
          </IconButton>
        </Tooltip>
        <Tooltip title="Actualizar agora">
          <IconButton size="small" onClick={refresh} disabled={loading}>
            <RefreshIcon style={{ fontSize: 16, color: '#1e40af', opacity: loading ? 0.4 : 1 }} />
          </IconButton>
        </Tooltip>
      </Box>

      <Box className={classes.scroll}>
        <Box className={classes.header}>
          <Typography className={classes.headerCell}>Veículo</Typography>
          <Typography className={classes.headerCell}>Motorista</Typography>
          <Typography className={classes.headerCell}>OT em curso</Typography>
          <Typography className={classes.headerCell}>Progresso</Typography>
          <Typography className={classes.headerCell}>GPS</Typography>
          <Typography className={classes.headerCell}>Última entrega</Typography>
          <Typography className={classes.headerCell}>Estado</Typography>
          <Typography className={classes.headerCell}></Typography>
        </Box>

        {filtered.length === 0 ? (
          <Box className={classes.emptyState}>
            <PersonIcon style={{ fontSize: 32, color: '#cbd5e1' }} />
            <Typography style={{ fontSize: '0.82rem', fontWeight: 600 }}>
              {summary.length === 0
                ? (lang === 'pt' ? 'Sem operações em curso' : 'No operations in progress')
                : (lang === 'pt' ? 'Sem resultados para a pesquisa' : 'No results for the search')}
            </Typography>
            <Typography style={{ fontSize: '0.7rem' }}>
              {lang === 'pt'
                ? 'Aparecem aqui as viaturas com rotas em curso ou planeadas.'
                : 'Vehicles with routes in progress or planned will appear here.'}
            </Typography>
          </Box>
        ) : (
          filtered.map(entry => (
            <OperationRow
              key={entry.vehicleId}
              entry={entry}
              vehiclePos={positionByVehicleId.get(Number(entry.vehicleId)) || null}
              onOpenLive={onOpenLive}
              onEditDriver={openEditDriver}
            />
          ))
        )}
      </Box>

      <DriverQuickEditDialog
        open={!!editDriver}
        onClose={() => setEditDriver(null)}
        driver={editDriver}
        token={token}
        lang={lang}
        onSaved={() => { fetchDrivers(); refresh(); }}
      />
    </Box>
  );
}
